import { useLocalization } from '@fluent/react';
import { useAppDispatch, useDataGridCustomLocaleText } from '../../../app/hooks';
import { useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ErrorContainer } from '../../../app/ErrorContainer';
import { LoadingContainer } from '../../../app/LoadingContainer';
import { Button, ButtonGroup, Paper, TableContainer } from '@mui/material';
import { QuickSearchToolbar } from '../../../app/QuickSearchToolbar';
import { FlexDiv } from '../../../app/Utils';
import { AddFabButton } from '../../../app/components/AddFabButton';
import { WorkflowRole } from './api/workflowTypes';
import { loadWorkflowRoles } from './api/workflowRolesApi';
import { WorkflowRoleDialog } from './dialogs/WorkflowRoleDialog';

export const WorkflowRolesList = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [wflRoles, setWflRoles] = useState<WorkflowRole[]>([]);
    const localeText = useDataGridCustomLocaleText();
    const [createDialogVisible, setCreateDialogVisible] = useState(false);
    const [editingRole, setEditingRole] = useState<WorkflowRole | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState(null);

    const loadData = () => {
        setIsLoading(true);
        dispatch(loadWorkflowRoles())
            .then(unwrapResult)
            .then(setWflRoles)
            .catch((error) => {
                console.log(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadData();
    }, []);

    const onSucces = () => {
        setCreateDialogVisible(false);
        setEditingRole(null);
        loadData();
    };

    const handleHideDialog = () => {
        setCreateDialogVisible(false);
        setEditingRole(null);
    };

    const openCreateDialog = () => {
        setCreateDialogVisible(true);
        setEditingRole(null);
    };

    const handleEditClick = (role: WorkflowRole) => {
        setEditingRole(role);
        setCreateDialogVisible(true);
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: l10n.getString('wfl-name', null, 'Name'),
            width: 200,
        },
        {
            field: 'code',
            headerName: l10n.getString('wfl-role-code', null, 'Code'),
            width: 200,
        },
        {
            field: 'actions',
            type: 'actions',
            width: 150,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        <Button
                            onClick={() => handleEditClick(params.row)}
                            color="primary"
                            variant="outlined"
                        >
                            {l10n.getString('edit', null, 'Edytuj')}
                        </Button>
                    </ButtonGroup>
                );
            },
        },
    ];

    if (isLoading) return <LoadingContainer />;
    if (isError) return <ErrorContainer error={error} />;

    return (
        <>
            <TableContainer component={Paper}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            style={{ minHeight: 100, height: 'calc(100vh - 250px)' }}
                            localeText={localeText}
                            rows={wflRoles}
                            columns={columns}
                            getRowId={(row) => row.id}
                            slots={{ toolbar: QuickSearchToolbar }}
                        />
                    </div>
                </div>
                <FlexDiv>
                    <AddFabButton onClick={openCreateDialog} />
                </FlexDiv>
            </TableContainer>

            {createDialogVisible && (
                <WorkflowRoleDialog
                    workflowRole={editingRole ?? undefined}
                    onSucces={onSucces}
                    onCancel={handleHideDialog}
                />
            )}
        </>
    );
};
